import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Badge, Alert, Card, Row, Col, ListGroup } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import VodService from '../../../services/VodService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PlaylistManagement = () => {
  const [playlists, setPlaylists] = useState([]);
  const [directories, setDirectories] = useState([]);
  const [directoryAssets, setDirectoryAssets] = useState([]);
  const [selectedDirectory, setSelectedDirectory] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistItems, setPlaylistItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    loop_enabled: false,
    shuffle_enabled: false
  });

  useEffect(() => {
    fetchPlaylists();
    fetchDirectories();
  }, []);

  const fetchPlaylists = async () => {
    try {
      setLoading(true);
      const response = await VodService.getAllPlaylists();
      if (response.success) {
        setPlaylists(response.playlists || []);
      }
    } catch (error) {
      toast.error('Failed to fetch playlists');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDirectories = async () => {
    try {
      const response = await VodService.getAllDirectories();
      if (response.success) {
        setDirectories(response.directories || []);
      }
    } catch (error) {
      console.error('Failed to fetch directories:', error);
    }
  };

  const fetchDirectoryAssets = async (directoryId) => {
    try {
      const response = await VodService.getAssetsByDirectory(directoryId);
      if (response.success) {
        setDirectoryAssets(response.assets || []);
      }
    } catch (error) {
      toast.error('Failed to fetch directory assets');
      console.error(error);
    }
  };

  const handleDirectoryChange = (directoryId) => {
    setSelectedDirectory(directoryId);
    if (directoryId) {
      fetchDirectoryAssets(directoryId);
    } else {
      setDirectoryAssets([]);
    }
  };

  const handleCreatePlaylist = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await VodService.createPlaylist({
        ...formData,
        items: playlistItems.map(item => item.id)
      });
      if (response.success) {
        toast.success('Playlist created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchPlaylists();
      }
    } catch (error) {
      toast.error('Failed to create playlist');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePlaylist = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await VodService.updatePlaylist(selectedPlaylist.id, {
        ...formData,
        items: playlistItems.map(item => item.id)
      });
      if (response.success) {
        toast.success('Playlist updated successfully');
        setShowEditModal(false);
        resetForm();
        fetchPlaylists();
      }
    } catch (error) {
      toast.error('Failed to update playlist');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlaylist = async (id) => {
    if (!window.confirm('Are you sure you want to delete this playlist?')) {
      return;
    }
    try {
      setLoading(true);
      const response = await VodService.deletePlaylist(id);
      if (response.success) {
        toast.success('Playlist deleted successfully');
        fetchPlaylists();
      }
    } catch (error) {
      toast.error('Failed to delete playlist');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const addToPlaylist = (asset) => {
    if (playlistItems.find(item => item.id === asset.id)) {
      toast.warning('This asset is already in the playlist');
      return;
    }
    setPlaylistItems([...playlistItems, asset]);
    toast.success(`Added "${asset.title}" to playlist`);
  };

  const removeFromPlaylist = (index) => {
    const newItems = [...playlistItems];
    newItems.splice(index, 1);
    setPlaylistItems(newItems);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const newItems = Array.from(playlistItems);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    setPlaylistItems(newItems);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = async (playlist) => {
    setSelectedPlaylist(playlist);
    setFormData({
      name: playlist.name,
      description: playlist.description || '',
      loop_enabled: playlist.loop_enabled === 1,
      shuffle_enabled: playlist.shuffle_enabled === 1
    });
    
    // Fetch full asset details for items
    if (playlist.items && playlist.items.length > 0) {
      const itemsWithDetails = await Promise.all(
        playlist.items.map(async (itemId) => {
          try {
            const response = await VodService.getAllAssets();
            const asset = response.assets?.find(a => a.id === itemId);
            return asset || { id: itemId, title: 'Unknown Asset' };
          } catch (error) {
            return { id: itemId, title: 'Unknown Asset' };
          }
        })
      );
      setPlaylistItems(itemsWithDetails);
    } else {
      setPlaylistItems([]);
    }
    
    setShowEditModal(true);
  };

  const viewPlaylistAssets = (playlist) => {
    setSelectedPlaylist(playlist);
    setShowAssetsModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      loop_enabled: false,
      shuffle_enabled: false
    });
    setPlaylistItems([]);
    setSelectedDirectory('');
    setDirectoryAssets([]);
    setSelectedPlaylist(null);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateTotalDuration = () => {
    return playlistItems.reduce((total, item) => total + (item.duration || 0), 0);
  };

  return (
    <div className="playlist-management">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Playlist Management</h2>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="flaticon-plus mr-2"></i> Create Playlist
        </Button>
      </div>

      <Alert variant="info">
        <i className="flaticon-information mr-2"></i>
        Create playlists to organize your VOD content. You can add assets from media directories, reorder them with drag & drop, and enable loop mode for continuous playback.
      </Alert>

      <Row>
        {playlists.map((playlist) => (
          <Col lg={4} md={6} key={playlist.id} className="mb-4">
            <Card className="playlist-card h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{playlist.name}</h5>
                <div>
                  {playlist.loop_enabled === 1 && (
                    <Badge variant="success" className="mr-1">Loop</Badge>
                  )}
                  {playlist.shuffle_enabled === 1 && (
                    <Badge variant="info">Shuffle</Badge>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <p className="text-muted">{playlist.description || 'No description'}</p>
                <p className="mb-0">
                  <i className="flaticon-video mr-1"></i>
                  <strong>{playlist.items?.length || 0}</strong> items
                </p>
              </Card.Body>
              <Card.Footer className="d-flex justify-content-between">
                <Button 
                  variant="outline-info" 
                  size="sm"
                  onClick={() => viewPlaylistAssets(playlist)}
                >
                  <i className="flaticon-view mr-1"></i> View
                </Button>
                <div>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    className="mr-2"
                    onClick={() => openEditModal(playlist)}
                  >
                    <i className="flaticon-edit"></i>
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => handleDeletePlaylist(playlist.id)}
                  >
                    <i className="flaticon-delete"></i>
                  </Button>
                </div>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>

      {playlists.length === 0 && !loading && (
        <div className="text-center py-5">
          <i className="flaticon-playlist" style={{ fontSize: '48px', color: '#ccc' }}></i>
          <p className="mt-3 text-muted">No playlists found. Create your first playlist to get started.</p>
        </div>
      )}

      {/* Create Playlist Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Create Playlist</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreatePlaylist}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Playlist Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter playlist name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Enable Loop"
                    checked={formData.loop_enabled}
                    onChange={(e) => setFormData({...formData, loop_enabled: e.target.checked})}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Enable Shuffle"
                    checked={formData.shuffle_enabled}
                    onChange={(e) => setFormData({...formData, shuffle_enabled: e.target.checked})}
                  />
                </Form.Group>
                <hr />
                <h6>Playlist Items ({playlistItems.length})</h6>
                <p className="text-muted small">
                  Total Duration: {formatDuration(calculateTotalDuration())}
                </p>
              </Col>
              <Col md={4}>
                <h6>Add from Directory</h6>
                <Form.Select 
                  className="mb-3"
                  value={selectedDirectory}
                  onChange={(e) => handleDirectoryChange(e.target.value)}
                >
                  <option value="">Select a directory...</option>
                  {directories.map(dir => (
                    <option key={dir.id} value={dir.id}>{dir.name}</option>
                  ))}
                </Form.Select>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {directoryAssets.map(asset => (
                    <Card key={asset.id} className="mb-2">
                      <Card.Body className="p-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <p className="mb-0 small">{asset.title}</p>
                            <p className="mb-0 text-muted small">
                              {formatDuration(asset.duration)}
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline-primary"
                            onClick={() => addToPlaylist(asset)}
                          >
                            <i className="flaticon-plus"></i>
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </Col>
              <Col md={4}>
                <h6>Playlist Order (Drag to reorder)</h6>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="playlist">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{ maxHeight: '400px', overflowY: 'auto' }}
                      >
                        {playlistItems.map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <Card className="mb-2">
                                  <Card.Body className="p-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <p className="mb-0 small">{item.title}</p>
                                        <p className="mb-0 text-muted small">
                                          {formatDuration(item.duration)}
                                        </p>
                                      </div>
                                      <Button 
                                        size="sm" 
                                        variant="outline-danger"
                                        onClick={() => removeFromPlaylist(index)}
                                      >
                                        <i className="flaticon-delete"></i>
                                      </Button>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading || playlistItems.length === 0}>
              {loading ? 'Creating...' : 'Create Playlist'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Playlist Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Edit Playlist</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdatePlaylist}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Playlist Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter playlist name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Enable Loop"
                    checked={formData.loop_enabled}
                    onChange={(e) => setFormData({...formData, loop_enabled: e.target.checked})}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Enable Shuffle"
                    checked={formData.shuffle_enabled}
                    onChange={(e) => setFormData({...formData, shuffle_enabled: e.target.checked})}
                  />
                </Form.Group>
                <hr />
                <h6>Playlist Items ({playlistItems.length})</h6>
                <p className="text-muted small">
                  Total Duration: {formatDuration(calculateTotalDuration())}
                </p>
              </Col>
              <Col md={4}>
                <h6>Add from Directory</h6>
                <Form.Select 
                  className="mb-3"
                  value={selectedDirectory}
                  onChange={(e) => handleDirectoryChange(e.target.value)}
                >
                  <option value="">Select a directory...</option>
                  {directories.map(dir => (
                    <option key={dir.id} value={dir.id}>{dir.name}</option>
                  ))}
                </Form.Select>
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {directoryAssets.map(asset => (
                    <Card key={asset.id} className="mb-2">
                      <Card.Body className="p-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <p className="mb-0 small">{asset.title}</p>
                            <p className="mb-0 text-muted small">
                              {formatDuration(asset.duration)}
                            </p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline-primary"
                            onClick={() => addToPlaylist(asset)}
                          >
                            <i className="flaticon-plus"></i>
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              </Col>
              <Col md={4}>
                <h6>Playlist Order (Drag to reorder)</h6>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="playlist-edit">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{ maxHeight: '400px', overflowY: 'auto' }}
                      >
                        {playlistItems.map((item, index) => (
                          <Draggable key={item.id} draggableId={`edit-${item.id}`} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <Card className="mb-2">
                                  <Card.Body className="p-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <p className="mb-0 small">{item.title}</p>
                                        <p className="mb-0 text-muted small">
                                          {formatDuration(item.duration)}
                                        </p>
                                      </div>
                                      <Button 
                                        size="sm" 
                                        variant="outline-danger"
                                        onClick={() => removeFromPlaylist(index)}
                                      >
                                        <i className="flaticon-delete"></i>
                                      </Button>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Playlist'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* View Assets Modal */}
      <Modal show={showAssetsModal} onHide={() => setShowAssetsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedPlaylist?.name} - Assets</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPlaylist?.items?.length === 0 ? (
            <Alert variant="info">No assets in this playlist.</Alert>
          ) : (
            <ListGroup>
              {selectedPlaylist?.items?.map((itemId, index) => (
                <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                  <span>Asset ID: {itemId}</span>
                  <Badge variant="primary">#{index + 1}</Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssetsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PlaylistManagement;
